import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {PostTypeEnum} from '../../core/enums/post-type.enum';
import {MediaTypeEnum} from '../../core/enums/media-type.enum';
import {FeedCollectionDataProviderService, FeedPageResult} from '../../shared/feed/feed-collection-data-provider.service';
import {TimelineDataProviderService, TimelinePageResult} from '../../shared/timeline/timeline.data-provider.service';

export type HomeContentTab = 'timeline' | 'feed' | 'media' | 'reels';

/** Keeps the first page of each home source available until its tab is mounted. */
@Injectable({providedIn: 'root'})
export class HomeContentPrefetchService {
  private readonly pages = new Map<HomeContentTab, Observable<TimelinePageResult | FeedPageResult>>();

  constructor(
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly feedDPS: FeedCollectionDataProviderService,
  ) {}

  public getTimelinePage(): Observable<TimelinePageResult> {
    return this.getOrCreate('timeline', () => this.timelineDPS.getPostCollection(
      null,
      null,
      1,
      [
        PostTypeEnum.SIMPLE_POST,
        PostTypeEnum.PROJECT_POST,
        PostTypeEnum.EVENT_DISCUSSION_POST,
        PostTypeEnum.PROJECT_MEDIA_POST,
      ]
    )) as Observable<TimelinePageResult>;
  }

  public getMediaPage(): Observable<TimelinePageResult> {
    return this.getOrCreate('media', () => this.timelineDPS.getPostCollection(
      null,
      null,
      1,
      [PostTypeEnum.PROJECT_MEDIA_POST]
    )) as Observable<TimelinePageResult>;
  }

  public getReelsPage(): Observable<TimelinePageResult> {
    return this.getOrCreate('reels', () => this.timelineDPS.getPostCollection(
      null,
      null,
      1,
      [PostTypeEnum.PROJECT_MEDIA_POST],
      null,
      MediaTypeEnum.VIDEO,
    )) as Observable<TimelinePageResult>;
  }

  public getFeedPage(): Observable<FeedPageResult> {
    return this.getOrCreate('feed', () => this.feedDPS.getPage(1, 20)) as Observable<FeedPageResult>;
  }

  /**
   * First-page prefetch is only a hand-off cache. Once Home owns the page in a
   * mounted tab/snapshot, keeping a root-level replay buffer retains an
   * unnecessary second path to the complete response graph.
   */
  public release(tab: HomeContentTab): void {
    this.pages.delete(tab);
  }

  private getOrCreate<T extends TimelinePageResult | FeedPageResult>(
    tab: HomeContentTab,
    factory: () => Observable<T>
  ): Observable<T> {
    const cached = this.pages.get(tab) as Observable<T> | undefined;
    if (cached) {
      return cached;
    }

    const page$ = factory().pipe(shareReplay({bufferSize: 1, refCount: false}));
    this.pages.set(tab, page$);
    return page$;
  }
}
